import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from '../config/dayjs';
// Usando dayjs para formateo de fechas
import { useCheque, useAuth, useNotification } from '../context';
import * as novedadService from '../services/novedadService';
import * as auditoriaService from '../services/auditoriaService';
import * as cambioFechaService from '../services/cambioFechaService';

// Mapeo de estados a colores
const ESTADO_COLORS = {
  PENDIENTE: 'warning',
  DEPOSITADO: 'info',
  RECHAZADO: 'error',
  COBRADO: 'success',
  CANCELADO: 'default',
};

const CheckDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getChequeById, deleteCheque, loading: chequeLoading } = useCheque();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [cheque, setCheque] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [novedades, setNovedades] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loadingNovedades, setLoadingNovedades] = useState(false);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cambioFechaDialogOpen, setCambioFechaDialogOpen] = useState(false);
  const [novedadDialogOpen, setNovedadDialogOpen] = useState(false);

  // Formulario de cambio de fecha
  const [nuevaFecha, setNuevaFecha] = useState(null);
  const [motivoCambioFecha, setMotivoCambioFecha] = useState('');

  // Formulario de novedad
  const [tipoNovedad, setTipoNovedad] = useState('');
  const [descripcionNovedad, setDescripcionNovedad] = useState('');

  // Cargar cheque
  useEffect(() => {
    const loadCheque = async () => {
      try {
        const data = await getChequeById(id);
        setCheque(data);
      } catch (error) {
        console.error('Error cargando cheque:', error);
        showError('Error al cargar el cheque');
      }
    };

    if (id) {
      loadCheque();
    }
  }, [id, getChequeById, showError]);

  // Cargar novedades cuando se selecciona el tab
  useEffect(() => {
    const loadNovedades = async () => {
      if (activeTab === 1 && id) {
        try {
          setLoadingNovedades(true);
          const data = await novedadService.getNovedadesByCheque(id);
          setNovedades(data);
        } catch (error) {
          console.error('Error cargando novedades:', error);
        } finally {
          setLoadingNovedades(false);
        }
      }
    };

    loadNovedades();
  }, [activeTab, id]);

  // Cargar historial cuando se selecciona el tab
  useEffect(() => {
    const loadHistorial = async () => {
      if (activeTab === 2 && id) {
        try {
          setLoadingHistorial(true);
          const data = await auditoriaService.getChequeHistory(id);
          setHistorial(data);
        } catch (error) {
          console.error('Error cargando historial:', error);
        } finally {
          setLoadingHistorial(false);
        }
      }
    };

    loadHistorial();
  }, [activeTab, id]);

  const handleBack = () => {
    navigate('/cheques');
  };

  const handleEdit = () => {
    navigate(`/cheques/editar/${id}`);
  };

  const handleDelete = async () => {
    try {
      await deleteCheque(id);
      showSuccess('Cheque eliminado correctamente');
      navigate('/cheques');
    } catch (error) {
      console.error('Error eliminando cheque:', error);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleCambioFecha = async () => {
    if (!nuevaFecha || !motivoCambioFecha) {
      showError('Debe completar todos los campos');
      return;
    }

    try {
      await cambioFechaService.requestDateChange({
        chequeId: id,
        fechaNueva: nuevaFecha,
        motivo: motivoCambioFecha,
      });
      showSuccess('Solicitud de cambio de fecha creada correctamente');
      setCambioFechaDialogOpen(false);
      setNuevaFecha(null);
      setMotivoCambioFecha('');
    } catch (error) {
      console.error('Error creando cambio de fecha:', error);
    }
  };

  const handleAgregarNovedad = async () => {
    if (!tipoNovedad || !descripcionNovedad) {
      showError('Debe completar todos los campos');
      return;
    }

    try {
      await novedadService.createNovedad(id, {
        tipo: tipoNovedad,
        descripcion: descripcionNovedad,
        fechaNovedad: new Date(),
      });
      showSuccess('Novedad creada correctamente');
      setNovedadDialogOpen(false);
      setTipoNovedad('');
      setDescripcionNovedad('');
      // Recargar novedades
      const data = await novedadService.getNovedadesByCheque(id);
      setNovedades(data);
    } catch (error) {
      console.error('Error creando novedad:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return dayjs(date).format('DD/MM/YYYY');
    } catch {
      return '-';
    }
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    try {
      return dayjs(date).format('DD/MM/YYYY HH:mm');
    } catch {
      return '-';
    }
  };

  const canEdit = () => {
    if (!cheque || !user) return false;
    return (
      cheque.estado === 'pendiente' &&
      (user.rol === 'administrador' || user.rol === 'contador')
    );
  };

  const canDelete = () => {
    if (!user) return false;
    return user.rol === 'administrador';
  };

  const canChangeFecha = () => {
    if (!user) return false;
    return user.rol === 'administrador' || user.rol === 'contador';
  };

  const canAddNovedad = () => {
    if (!user) return false;
    return user.rol === 'administrador' || user.rol === 'contador';
  };

  if (chequeLoading || !cheque) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ p: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/dashboard')}
            sx={{ textDecoration: 'none' }}
          >
            Dashboard
          </Link>
          <Link
            component="button"
            variant="body1"
            onClick={handleBack}
            sx={{ textDecoration: 'none' }}
          >
            Cheques
          </Link>
          <Typography color="text.primary">Detalle</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">
              Cheque #{cheque.numero_cheque}
            </Typography>
            <Chip
              label={cheque.estado}
              color={ESTADO_COLORS[cheque.estado] || 'default'}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {canEdit() && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Editar
              </Button>
            )}
            {canChangeFecha() && (
              <Button
                variant="outlined"
                startIcon={<CalendarIcon />}
                onClick={() => setCambioFechaDialogOpen(true)}
              >
                Cambiar Fecha
              </Button>
            )}
            {canAddNovedad() && (
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={() => setNovedadDialogOpen(true)}
              >
                Agregar Novedad
              </Button>
            )}
            {canDelete() && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Eliminar
              </Button>
            )}
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="Información General" />
            <Tab label="Novedades" />
            <Tab label="Historial de Cambios" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Información del Cheque */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Información del Cheque
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Número de Cheque
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {cheque.numero_cheque}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Monto
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formatCurrency(cheque.monto)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Banco
                      </Typography>
                      <Typography variant="body1">
                        {cheque.banco?.nombre || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Cuenta Origen
                      </Typography>
                      <Typography variant="body1">
                        {cheque.numero_cuenta_origen || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Fecha de Emisión
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(cheque.fecha_emision)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Fecha de Pago
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(cheque.fecha_pago)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Dueño de la Cuenta
                      </Typography>
                      <Typography variant="body1">
                        {cheque.nombre_dueno_cuenta || '-'}
                      </Typography>
                    </Grid>
                    {cheque.observaciones && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Observaciones
                        </Typography>
                        <Typography variant="body1">
                          {cheque.observaciones}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Información del Cliente */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Información del Cliente
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Nombre/Razón Social
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {cheque.cliente?.nombre || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        RUC
                      </Typography>
                      <Typography variant="body1">
                        {cheque.cliente?.ruc || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Teléfono
                      </Typography>
                      <Typography variant="body1">
                        {cheque.cliente?.telefono || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1">
                        {cheque.cliente?.email || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Dirección
                      </Typography>
                      <Typography variant="body1">
                        {cheque.cliente?.direccion || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Estado y Fechas */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Estado y Fechas
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Estado
                      </Typography>
                      <Typography variant="body1">
                        <Chip
                          label={cheque.estado}
                          color={ESTADO_COLORS[cheque.estado] || 'default'}
                          size="small"
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Cajero
                      </Typography>
                      <Typography variant="body1">
                        {cheque.cajero?.nombre || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Fecha de Creación
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(cheque.createdAt)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Última Actualización
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(cheque.updatedAt)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Características */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Características
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {cheque.es_cruzado && (
                      <Chip label="Cruzado" color="primary" variant="outlined" />
                    )}
                    {cheque.es_endosado && (
                      <Chip label="Endosado" color="primary" variant="outlined" />
                    )}
                    {cheque.es_postfechado && (
                      <Chip label="Post-fechado" color="secondary" variant="outlined" />
                    )}
                    {!cheque.es_cruzado && !cheque.es_endosado && !cheque.es_postfechado && (
                      <Typography variant="body2" color="text.secondary">
                        Sin características especiales
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Novedades del Cheque
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {loadingNovedades ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress />
                </Box>
              ) : novedades.length === 0 ? (
                <Typography color="text.secondary" align="center" py={3}>
                  No hay novedades registradas
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Usuario</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {novedades.map((novedad) => (
                        <TableRow key={novedad._id}>
                          <TableCell>{novedad.tipo}</TableCell>
                          <TableCell>{novedad.descripcion}</TableCell>
                          <TableCell>{formatDateTime(novedad.fechaNovedad)}</TableCell>
                          <TableCell>
                            <Chip
                              label={novedad.estado}
                              color={novedad.estado === 'RESUELTA' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{novedad.usuario?.nombre || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Historial de Cambios
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {loadingHistorial ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress />
                </Box>
              ) : historial.length === 0 ? (
                <Typography color="text.secondary" align="center" py={3}>
                  No hay cambios registrados
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Acción</TableCell>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Detalles</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historial.map((log) => (
                        <TableRow key={log._id}>
                          <TableCell>{formatDateTime(log.fecha)}</TableCell>
                          <TableCell>
                            <Chip label={log.accion} size="small" />
                          </TableCell>
                          <TableCell>{log.usuario?.nombre || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {log.descripcion || 'Sin detalles'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* Dialog de eliminación */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirmar Eliminación</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Está seguro que desea eliminar este cheque? Esta acción no se puede deshacer.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de cambio de fecha */}
        <Dialog
          open={cambioFechaDialogOpen}
          onClose={() => setCambioFechaDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Cambiar Fecha de Pago</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <DatePicker
                    label="Nueva Fecha de Pago"
                    value={nuevaFecha}
                    onChange={setNuevaFecha}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Motivo del Cambio"
                    multiline
                    rows={3}
                    value={motivoCambioFecha}
                    onChange={(e) => setMotivoCambioFecha(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCambioFechaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCambioFecha} variant="contained">
              Solicitar Cambio
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de agregar novedad */}
        <Dialog
          open={novedadDialogOpen}
          onClose={() => setNovedadDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Agregar Novedad</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    select
                    label="Tipo de Novedad"
                    value={tipoNovedad}
                    onChange={(e) => setTipoNovedad(e.target.value)}
                  >
                    <MenuItem value="RECHAZO">Rechazo</MenuItem>
                    <MenuItem value="DEVOLUCION">Devolución</MenuItem>
                    <MenuItem value="OBSERVACION">Observación</MenuItem>
                    <MenuItem value="OTRO">Otro</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Descripción"
                    multiline
                    rows={4}
                    value={descripcionNovedad}
                    onChange={(e) => setDescripcionNovedad(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNovedadDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAgregarNovedad} variant="contained">
              Agregar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default CheckDetailPage;
